import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, CONFIDENCE_CLS, FRESHNESS_CLS, TEST_TYPES, TEST_RESULTS, tTestResult } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ShieldQuestion, Layers, History, ChevronDown, CheckCircle2, XCircle } from "lucide-react";

const EMPTY = { alternative_target_id: "", status: "non_verifiee", priority: 1, switchover_mode: "non_renseignee", validation: "declaree", description: "", failure_domain: "" };
const EMPTY_TEST = { tested_at: "", test_type: "complet", result: "reussi", switchover_seconds: "", rto_target_minutes: "", rto_observed_minutes: "", initiator: "", comment: "", evidence_ref: "" };

function CommonDomains({ list }) {
  if (!list?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="alt-common-domains">
      <span className="inline-flex items-center gap-1 text-[11px] text-yellow-400"><Layers className="h-3 w-3" /> Domaine(s) commun(s) :</span>
      {list.map((c) => (
        <Badge key={c.id} variant="outline" className="rounded-sm text-[10px] border-yellow-500/40 text-yellow-300">{c.type_label} : {c.name}</Badge>
      ))}
    </div>
  );
}

function FailoverTests({ altId, writable }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_TEST);

  const { data: tests = [] } = useQuery({
    queryKey: ["failover-tests", altId],
    queryFn: async () => (await api.get("/failover/tests", { params: { alternative_id: altId } })).data,
    enabled: open && !!altId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["failover-tests", altId] });
    qc.invalidateQueries({ queryKey: ["alternatives"] });
    qc.invalidateQueries({ queryKey: ["service-impact"] });
  };
  const create = useMutation({
    mutationFn: async () => (await api.post("/failover/tests", {
      ...form,
      switchover_seconds: form.switchover_seconds ? Number(form.switchover_seconds) : null,
      rto_target_minutes: form.rto_target_minutes ? Number(form.rto_target_minutes) : null,
      rto_observed_minutes: form.rto_observed_minutes ? Number(form.rto_observed_minutes) : null,
    }, { params: { alternative_id: altId } })).data,
    onSuccess: () => { toast.success("Test de bascule enregistré"); setForm(EMPTY_TEST); setAdding(false); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div className="mt-3 border-t border-border pt-2">
      <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => setOpen(!open)} data-testid={`alt-tests-toggle-${altId}`}>
        <History className="h-3.5 w-3.5" /> Tests de bascule <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </Button>
      {open && (
        <div className="mt-2 space-y-2">
          {tests.length === 0 && <p className="text-[11px] text-muted-foreground">Aucun test enregistré. L'historique est conservé (jamais écrasé).</p>}
          {tests.map((t) => {
            const rc = TEST_RESULTS[t.result] || TEST_RESULTS.non_concluant;
            const Icon = t.result === "reussi" ? CheckCircle2 : t.result === "echoue" ? XCircle : History;
            return (
              <div key={t.id} className="flex items-start gap-2 text-[11px] border border-border rounded-sm p-2" data-testid={`failover-test-${t.id}`}>
                <Icon className={`h-3.5 w-3.5 mt-0.5 ${t.result === "reussi" ? "text-green-400" : t.result === "echoue" ? "text-red-400" : "text-muted-foreground"}`} />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${rc.cls}`}>{rc.label}</Badge>
                    <span className="text-muted-foreground">{t.tested_at ? new Date(t.tested_at).toLocaleDateString("fr-FR") : "—"}</span>
                    <span className="font-mono text-muted-foreground">{t.test_type_label}</span>
                    {t.rto_observed_minutes != null && <span className="text-muted-foreground">RTO obs. {t.rto_observed_minutes} min{t.rto_target_minutes != null ? ` / cible ${t.rto_target_minutes} min` : ""}</span>}
                  </div>
                  {t.comment && <p className="text-muted-foreground mt-0.5">{t.comment}</p>}
                  {t.initiator && <p className="text-muted-foreground/70">Initié par {t.initiator}</p>}
                </div>
              </div>
            );
          })}

          {writable && (adding ? (
            <div className="border border-border rounded-sm p-2 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Date du test</Label><Input type="date" value={form.tested_at} onChange={(e) => setForm({ ...form, tested_at: e.target.value })} className="mt-1 rounded-sm h-8 text-xs" data-testid="test-date-input" /></div>
                <div>
                  <Label className="text-[10px]">Type</Label>
                  <Select value={form.test_type} onValueChange={(v) => setForm({ ...form, test_type: v })}>
                    <SelectTrigger className="mt-1 rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TEST_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Résultat</Label>
                  <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                    <SelectTrigger className="mt-1 rounded-sm h-8 text-xs" data-testid="test-result-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TEST_RESULTS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px]">Bascule (s)</Label><Input type="number" min="0" value={form.switchover_seconds} onChange={(e) => setForm({ ...form, switchover_seconds: e.target.value })} className="mt-1 rounded-sm h-8 text-xs" /></div>
                <div><Label className="text-[10px]">RTO cible (min)</Label><Input type="number" min="0" value={form.rto_target_minutes} onChange={(e) => setForm({ ...form, rto_target_minutes: e.target.value })} className="mt-1 rounded-sm h-8 text-xs" /></div>
                <div><Label className="text-[10px]">RTO observé (min)</Label><Input type="number" min="0" value={form.rto_observed_minutes} onChange={(e) => setForm({ ...form, rto_observed_minutes: e.target.value })} className="mt-1 rounded-sm h-8 text-xs" /></div>
              </div>
              <div><Label className="text-[10px]">Initiateur</Label><Input value={form.initiator} onChange={(e) => setForm({ ...form, initiator: e.target.value })} className="mt-1 rounded-sm h-8 text-xs" /></div>
              <div><Label className="text-[10px]">Commentaire</Label><Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="mt-1 rounded-sm text-xs" rows={2} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setAdding(false)}>Annuler</Button>
                <Button size="sm" className="rounded-sm" onClick={() => create.mutate()} disabled={!form.tested_at || create.isPending} data-testid="save-test-button">Enregistrer le test</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="rounded-sm gap-2 w-full" onClick={() => setAdding(true)} data-testid={`add-test-button-${altId}`}><Plus className="h-3.5 w-3.5" /> Ajouter un test de bascule</Button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * AlternativeManager — gère les RELATIONSHIP_ALTERNATIVE d'une relation principale.
 * props: { open, onOpenChange, primaryEdgeId, primaryLabel, options:[{id,name}], onChanged }
 */
export default function AlternativeManager({ open, onOpenChange, primaryEdgeId, primaryLabel, options = [], onChanged }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const writable = can("dependency:write");
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);

  const { data: alts = [] } = useQuery({
    queryKey: ["alternatives", primaryEdgeId],
    queryFn: async () => (await api.get("/alternatives", { params: { primary_edge_id: primaryEdgeId } })).data,
    enabled: open && !!primaryEdgeId,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["alternatives", primaryEdgeId] }); onChanged?.(); };

  const create = useMutation({
    mutationFn: async () => (await api.post("/alternatives", { primary_edge_id: primaryEdgeId, ...form })).data,
    onSuccess: () => { toast.success("Alternative ajoutée"); setForm(EMPTY); setAdding(false); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const patch = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/alternatives/${id}`, body)).data,
    onSuccess: () => { toast.success("Alternative mise à jour"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/alternatives/${id}`),
    onSuccess: () => { toast.success("Alternative supprimée"); refresh(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="alternative-manager">
        <DialogHeader>
          <DialogTitle className="font-head">Alternatives de la relation</DialogTitle>
          <DialogDescription>{primaryLabel} — une alternative déclarée n'équivaut pas à une redondance testée.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 max-h-[55vh] overflow-y-auto cros-scroll">
          {alts.length === 0 && <p className="text-sm text-muted-foreground">Aucune alternative enregistrée.</p>}
          {alts.map((a) => (
            <div key={a.id} className="border border-border rounded-sm p-3" data-testid={`alt-item-${a.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 font-medium text-sm">
                  <ShieldQuestion className="h-4 w-4 text-primary" /> {a.target_name}
                  <Badge variant="outline" className="rounded-sm text-[10px]">Priorité {a.priority}</Badge>
                </div>
                {writable && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => del.mutate(a.id)} data-testid={`alt-del-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-muted-foreground">Qualification :</span>
                <Badge variant="outline" className="rounded-sm text-[10px]" data-testid={`alt-qualification-${a.id}`}>{a.qualification_label}</Badge>
              </div>
              <CommonDomains list={a.common_domains} />

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Statut</Label>
                  <Select value={a.status} onValueChange={(v) => patch.mutate({ id: a.id, body: { status: v } })} disabled={!writable}>
                    <SelectTrigger className="mt-1 rounded-sm h-8 text-xs" data-testid={`alt-status-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disponible">Disponible</SelectItem>
                      <SelectItem value="degradee">Dégradée</SelectItem>
                      <SelectItem value="indisponible">Indisponible</SelectItem>
                      <SelectItem value="non_verifiee">Non vérifiée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Validation</Label>
                  <Select value={a.validation} onValueChange={(v) => patch.mutate({ id: a.id, body: { validation: v } })} disabled={!writable}>
                    <SelectTrigger className="mt-1 rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="declaree">Déclarée</SelectItem>
                      <SelectItem value="verifiee_techniquement">Vérifiée techniquement</SelectItem>
                      <SelectItem value="testee">Testée</SelectItem>
                      <SelectItem value="non_verifiee">Non vérifiée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Bascule</Label>
                  <Select value={a.switchover_mode} onValueChange={(v) => patch.mutate({ id: a.id, body: { switchover_mode: v } })} disabled={!writable}>
                    <SelectTrigger className="mt-1 rounded-sm h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatique">Automatique</SelectItem>
                      <SelectItem value="manuelle">Manuelle</SelectItem>
                      <SelectItem value="non_renseignee">Non renseignée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Confiance</Label>
                  <div className={`mt-1 text-sm font-semibold ${CONFIDENCE_CLS[a.confidence] || "text-muted-foreground"}`} data-testid={`alt-confidence-${a.id}`}>{a.confidence}</div>
                </div>
              </div>

              {a.last_test && (
                <p className="mt-2 text-[11px]" data-testid={`alt-last-test-${a.id}`}>
                  <span className="text-muted-foreground">Dernier test : </span>
                  <span className={a.last_test.result === "reussi" ? "text-green-400" : a.last_test.result === "echoue" ? "text-red-400" : "text-yellow-400"}>{tTestResult(a.last_test.result)}</span>
                  <span className="text-muted-foreground"> le {a.last_test.tested_at ? new Date(a.last_test.tested_at).toLocaleDateString("fr-FR") : "—"}</span>
                </p>
              )}
              {a.freshness && <p className={`text-[11px] ${FRESHNESS_CLS[a.freshness.status] || "text-muted-foreground"}`}>Fraîcheur : {a.freshness.label}</p>}

              <ul className="mt-2 text-[11px] text-muted-foreground list-disc pl-4">
                {a.confidence_criteria?.map((c, i) => <li key={i}>{c}</li>)}
              </ul>

              <FailoverTests altId={a.id} writable={writable} />
            </div>
          ))}
        </div>

        {writable && (adding ? (
          <div className="border-t border-border pt-3 space-y-3">
            <div>
              <Label className="text-xs">Actif alternatif</Label>
              <Select value={form.alternative_target_id} onValueChange={(v) => setForm({ ...form, alternative_target_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="alt-target-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent className="max-h-56">{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponible">Disponible</SelectItem>
                    <SelectItem value="degradee">Dégradée</SelectItem>
                    <SelectItem value="indisponible">Indisponible</SelectItem>
                    <SelectItem value="non_verifiee">Non vérifiée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Validation</Label>
                <Select value={form.validation} onValueChange={(v) => setForm({ ...form, validation: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="declaree">Déclarée</SelectItem>
                    <SelectItem value="verifiee_techniquement">Vérifiée techniquement</SelectItem>
                    <SelectItem value="testee">Testée</SelectItem>
                    <SelectItem value="non_verifiee">Non vérifiée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setAdding(false)}>Annuler</Button>
              <Button className="rounded-sm" onClick={() => create.mutate()} disabled={!form.alternative_target_id || create.isPending} data-testid="alt-save-button">Ajouter</Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" className="rounded-sm gap-2 w-full" onClick={() => setAdding(true)} data-testid="alt-add-button"><Plus className="h-4 w-4" /> Ajouter une alternative</Button>
        ))}

        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
